import * as React from "react";
import { KEYBOARD_KEYS } from "constants/keyboard";
import { ICellEditor, ICellEditorParams } from "ag-grid-community";
import { IChoiceValue } from "./single-choice-cell-editor";
import { OrderedSet } from "immutable";
import { formatSizePackValues, parseSizePacks } from "helpers/product-collection";
import { MultipleChoiceCellEditor } from "./multiple-choice-cell-editor";
import { withRouter } from "react-router";
import { connect } from "react-redux";
import { IStore } from "reducers";
import { amphibiousGetIn, amphibiousSetIn } from "helpers/objUtils";

interface IProps extends ICellEditorParams {
    getAvailableValues: (stateChunk: any, itemData: any) => IChoiceValue[];
    delimiter?: string;
    fetchSuggestions?: (...args: any[]) => { type: string; [key: string]: any } | undefined;
    statePaths?: string[][];
    onSelectedChanged?: (selected: any) => void;
}

interface IContainerProps {
    setValue: (value: string) => void;
}

interface IReduxProps {
    onFetchSuggestions?: (stateChunk: any) => void;
    stateChunk?: any;
}

interface IState {
    availableValues: IChoiceValue[];
    checkedValues: OrderedSet<string>;
}

class MultipleChoiceCellEditorComponent
    extends React.PureComponent<IProps & IReduxProps & IContainerProps, IState>
    implements ICellEditor
{
    constructor(props) {
        super(props);
        let checkedValues = props.value
            ? OrderedSet(parseSizePacks(props.value, props.delimiter))
            : OrderedSet<string>();
        const availableValues = props.getAvailableValues(props.stateChunk, props.data);
        const { eventKey } = this.props;

        if (eventKey === KEYBOARD_KEYS.BACKSPACE || eventKey === KEYBOARD_KEYS.DELETE) {
            checkedValues = OrderedSet();
        }

        this.state = { checkedValues, availableValues };
    }

    getValue = () => {
        return formatSizePackValues(this.state.checkedValues);
    };

    handleCheckboxChange = (value) => {
        const { checkedValues } = this.state;
        const newCheckedValues = {
            checkedValues: checkedValues.has(value) ? checkedValues.remove(value) : checkedValues.add(value),
        };
        const values = newCheckedValues.checkedValues.map((data) => data).toArray();
        this.setState(newCheckedValues);
        if (this.props.onSelectedChanged) {
            this.props.onSelectedChanged(values.join());
        }
    };

    handleSelectAll = () => {
        const { checkedValues, availableValues } = this.state;
        this.setState({
            checkedValues: OrderedSet(
                checkedValues.size === availableValues.length ? [] : availableValues.map(({ id }) => id)
            ),
        });
        const newCheckedValues = {
            checkedValues: OrderedSet(
                checkedValues.size === availableValues.length ? [] : availableValues.map(({ id }) => id)
            ),
        };
        this.setState(newCheckedValues);
        if (this.props.onSelectedChanged) {
            const values = newCheckedValues.checkedValues.map((data) => data);
            this.props.onSelectedChanged(values.join());
        }
    };

    render() {
        const { checkedValues, availableValues } = this.state;
        return (
            <MultipleChoiceCellEditor
                availableValues={availableValues}
                checkedValues={checkedValues}
                onSelectAll={this.handleSelectAll}
                onSelect={this.handleCheckboxChange}
            />
        );
    }

    componentDidMount() {
        const { onFetchSuggestions, stateChunk } = this.props;
        onFetchSuggestions?.(stateChunk);
    }

    componentDidUpdate(prevProps) {
        const { data, getAvailableValues, stateChunk } = this.props;
        if (prevProps.stateChunk !== stateChunk) {
            const newValues = getAvailableValues(stateChunk, data);
            this.setState({ availableValues: newValues });
        }
    }
}

const mapDispatchToProps = (dispatch, ownProps: IProps) => {
    const { fetchSuggestions, data } = ownProps;
    return {
        ...(fetchSuggestions
            ? {
                  onFetchSuggestions(stateChunk) {
                      const suggestionsAction = fetchSuggestions(stateChunk, data);
                      suggestionsAction && dispatch(suggestionsAction);
                  },
              }
            : {}),
    };
};

const mapStateToProps = (state: IStore, ownProps: IProps) => {
    const { statePaths } = ownProps;
    if (statePaths) {
        return {
            stateChunk: statePaths.reduce((acc, path) => {
                amphibiousSetIn(acc, path, amphibiousGetIn(state, path));
                return acc;
            }, {}),
        };
    }
    return {};
};

const MultipleChoiceCellEditorContainer = withRouter(
    connect(mapStateToProps, mapDispatchToProps, null, { withRef: true })(MultipleChoiceCellEditorComponent),
    {
        withRef: true,
    }
);

export class MultipleChoiceCellEditorWrapper extends React.PureComponent<IProps> {
    private containerRef;
    constructor(props) {
        super(props);
    }

    attachRef = (element) => {
        if (element) {
            const wrappedContainer = element.getWrappedInstance();
            this.containerRef = wrappedContainer?.wrappedInstance;
        }
    };

    getValue() {
        return this.containerRef?.getValue() ?? this.props.value;
    }

    isPopup(): boolean {
        return true;
    }

    render() {
        return <MultipleChoiceCellEditorContainer {...this.props} ref={this.attachRef} />;
    }
}
